import { detectLanguage } from './detect-language';
import { extractShortcode } from './parse-instagram-url';

import type { ReportRow } from './export-account-report-sheets';
import type { MediaStats, MediaStatsError } from '../types/statistics';

/**
 * «Отчёт министру» по списку ссылок.
 *
 * Ссылки наших подключённых пабликов (Instagram, TikTok) заполняются сами:
 * просмотры, дата, превью и тема — как в отчёте по аккаунту. Остальное
 * (партнёры, YouTube, сайты, чужие паблики) InstaFarm посчитать не может:
 * такие строки ждут просмотров и скрина от человека.
 */

export type LinkKind = 'instagram' | 'tiktok' | 'youtube' | 'other';

export type LinkRow = {
  url: string;
  kind: LinkKind;
  /** auto — цифры из InstaFarm; manual — заполняет человек. */
  source: 'auto' | 'manual';
  /** Почему строка ручная — показывается рядом со ссылкой. */
  reason: string | null;
  views: number | null;
  publishedAt: string;
  theme: string;
  imageUrl: string;
  /** Для общего кэша тем и превью — только у auto. */
  mediaId: string | null;
  thumbnailUrl: string | null;
  caption: string | null;
};

export function classifyLink(url: string): LinkKind {
  const host = (() => {
    try {
      return new URL(url).hostname.toLowerCase();
    } catch {
      return '';
    }
  })();
  if (/(^|\.)instagram\.com$/.test(host)) return 'instagram';
  if (/(^|\.)tiktok\.com$/.test(host)) return 'tiktok';
  if (/(^|\.)(youtube\.com|youtu\.be)$/.test(host)) return 'youtube';
  return 'other';
}

export function youtubeVideoId(url: string): string | null {
  const m =
    url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ??
    url.match(/youtube\.com\/(?:shorts|live|embed)\/([A-Za-z0-9_-]{6,})/) ??
    url.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  return m?.[1] ?? null;
}

/** Ссылки из вставленного текста: по одной на строку, без повторов, порядок сохраняем. */
export function parseLinks(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of text.matchAll(/https?:\/\/[^\s<>"'()[\]]+/g)) {
    const url = m[0].replace(/[.,;:!?]+$/, '');
    const key = url.replace(/[?#].*$/, '').replace(/\/+$/, '').toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(url);
  }
  return out;
}

const MANUAL_REASON: Record<LinkKind, string> = {
  instagram: 'Паблик не подключён к InstaFarm',
  tiktok: 'Аккаунт не подключён к InstaFarm',
  youtube: 'YouTube: просмотры вручную',
  other: 'Не наш паблик: просмотры и скрин вручную',
};

function emptyRow(url: string, kind: LinkKind, reason: string): LinkRow {
  return {
    url,
    kind,
    source: 'manual',
    reason,
    views: null,
    publishedAt: '',
    theme: '',
    imageUrl: '',
    mediaId: null,
    thumbnailUrl: null,
    caption: null,
  };
}

function matchKey(url: string): string {
  return extractShortcode(url) ?? url.replace(/[?#].*$/, '').replace(/\/+$/, '').toLowerCase();
}

/**
 * Строки отчёта в порядке ссылок. Результат InstaFarm без цифр (Business
 * Discovery у чужого паблика, недоступные метрики) — тоже ручная строка.
 */
export function buildLinkRows(urls: string[], results: MediaStats[], errors: MediaStatsError[]): LinkRow[] {
  const byKey = new Map<string, MediaStats>();
  for (const r of results) {
    byKey.set(matchKey(r.url), r);
    if (r.shortcode) byKey.set(r.shortcode, r);
  }
  const errorByKey = new Map(errors.map((e) => [matchKey(e.url), e.error]));

  return urls.map((url) => {
    const kind = classifyLink(url);
    if (kind !== 'instagram' && kind !== 'tiktok') return emptyRow(url, kind, MANUAL_REASON[kind]);

    const key = matchKey(url);
    const r = byKey.get(key);
    if (!r || r.isPartial) {
      return emptyRow(url, kind, errorByKey.get(key) ?? MANUAL_REASON[kind]);
    }
    // Как в отчёте по аккаунту: просмотры, а при нуле — охват.
    const views = r.impressionsTotal || r.reach || 0;
    return {
      url,
      kind,
      source: 'auto',
      reason: null,
      views,
      publishedAt: r.publishedAt ?? '',
      theme: '',
      imageUrl: '',
      mediaId: r.mediaId || r.shortcode,
      thumbnailUrl: r.thumbnailUrl ?? r.mediaUrl,
      caption: r.caption,
    };
  });
}

/** Ручные строки без просмотров — без них Word молча положил бы ссылку в «низкие». */
export function rowsMissingViews(rows: LinkRow[]): LinkRow[] {
  return rows.filter((r) => r.views == null || !Number.isFinite(r.views));
}

export function toReportRows(rows: LinkRow[]): ReportRow[] {
  return rows.map((r) => ({
    permalink: r.url,
    publishedAt: r.publishedAt,
    views: r.views ?? 0,
    theme: r.theme,
    language: r.theme ? detectLanguage(r.theme) : '',
    imageUrl: r.imageUrl,
  }));
}
